/**
 * DTMFKeypad — overlay numpad voor DTMF-tonen tijdens actieve call.
 *
 * Gebruik: medewerker navigeert IVR-menu's ("druk 1 voor X"). De knoppen
 * sturen via `sendDTMF(clientCallId, digit)` de digit door de SDK naar de
 * bridged call.
 */

import { useEffect, useRef } from 'react';

const KEYS: ReadonlyArray<{ digit: string; sub?: string; label: string }> = [
  { digit: '1', label: 'Een' },
  { digit: '2', sub: 'ABC', label: 'Twee' },
  { digit: '3', sub: 'DEF', label: 'Drie' },
  { digit: '4', sub: 'GHI', label: 'Vier' },
  { digit: '5', sub: 'JKL', label: 'Vijf' },
  { digit: '6', sub: 'MNO', label: 'Zes' },
  { digit: '7', sub: 'PQRS', label: 'Zeven' },
  { digit: '8', sub: 'TUV', label: 'Acht' },
  { digit: '9', sub: 'WXYZ', label: 'Negen' },
  { digit: '*', label: 'Ster' },
  { digit: '0', sub: '+', label: 'Nul' },
  { digit: '#', label: 'Hekje' },
];

interface DTMFKeypadProps {
  onDigit: (digit: string) => void;
  onClose: () => void;
}

export function DTMFKeypad({ onDigit, onClose }: DTMFKeypadProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus eerste knop bij openen voor keyboard-users.
  useEffect(() => {
    const first = containerRef.current?.querySelector<HTMLButtonElement>('button[data-digit]');
    first?.focus();
  }, []);

  // Keyboard: 0-9, * en # sturen via onDigit; Esc sluit.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (/^[0-9*#]$/.test(e.key)) {
        // Negeer als focus in een input zit (voorkomt race met andere forms).
        const target = e.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
        e.preventDefault();
        onDigit(e.key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDigit, onClose]);

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label="DTMF-toetsenbord"
      className="bg-white border-t border-gray-200 p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-600">Toetsen (druk op cijfer of toets)</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Toetsenbord sluiten"
          className="text-xs text-gray-500 hover:text-gray-800 px-2 py-0.5 rounded hover:bg-gray-100"
        >
          Sluiten (Esc)
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {KEYS.map((k) => (
          <button
            key={k.digit}
            type="button"
            data-digit={k.digit}
            onClick={() => onDigit(k.digit)}
            aria-label={k.label}
            className="aspect-[5/3] rounded-md bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-800 flex flex-col items-center justify-center leading-none transition-colors"
          >
            <span className="text-lg font-semibold">{k.digit}</span>
            {k.sub && <span className="text-[9px] tracking-widest text-gray-500 mt-0.5">{k.sub}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
